<template>
  <div>
    <d-form>
      <d-form-row class="form-group">
        <d-col cols="12" md="8">
          <label>Nome:</label>
          <d-form-input
            ref="nameInput"
            type="text"
            v-model="model.name"
            :state="hasErrorFiled('name')"
            placeholder="Ex: Number Square Nível 1"
          />
          <d-form-invalid-feedback>Nome é obrigatório</d-form-invalid-feedback>
        </d-col>
        <d-col
          cols="12"
          md="4"
          class="d-flex align-items-end"
        >
          <d-checkbox
            toggle
            v-model="model.settings.showAnswer"
            class="custom-toggle-sm"
          >
            <span>Mostrar resposta</span>
          </d-checkbox>
          <d-checkbox
            toggle
            v-model="model.settings.colored"
            class="custom-toggle-sm ml-4"
          >
            <span>Colorido</span>
          </d-checkbox>
        </d-col>
      </d-form-row>

      <d-form-row class="form-group align-items-start mt-3">
        <d-col cols="12" md="4">
          <label>Números disponíveis:</label>
          <d-select v-model="model.settings.maxValue" class="form-control">
            <option :value="9">1 até 9</option>
            <option :value="16">1 até 16</option>
            <option :value="25">1 até 25</option>
          </d-select>
        </d-col>
        <d-col cols="12" md="4">
          <label>Dificuldade:</label>
          <d-select
            v-model="model.settings.difficulty"
            class="form-control"
            @change="applyDifficultyWeights"
            :disabled="model.settings.enablePercentageDistribution"
          >
            <option value="easy">Fácil</option>
            <option value="medium">Médio</option>
            <option value="hard">Difícil</option>
            <option value="random">Aleatório</option>
          </d-select>
        </d-col>
        <d-col cols="12" md="4">
          <distribution-by-percentage
            v-model="model.settings.operatorWeights"
            :enabledByDefault.sync="model.settings.enablePercentageDistribution"
            :enabled-keys="enabledOperators"
            :labels="operatorLabels"
            checkboxLabel="Distribuir por porcentagem"
            tooltipText="Os operadores matemáticos serão distribuídos no jogo conforme as porcentagens definidas abaixo."
          />
        </d-col>
      </d-form-row>
    </d-form>
    <div class="position-relative mt-4">
      <board-grid
        v-if="model.table.length"
        :table="model.table"
        :rows="model.rows"
        :columns="model.columns"
        :cell-size="cellSize"
        :settings="model.settings"
        :show-border="false"
      />
    </div>
  </div>
</template>

<script>
// eslint-disable-next-line import/no-webpack-loader-syntax
import NumberSquareWorker from 'worker-loader!_utils/numberSquare/worker.js';
import { mapActions } from 'vuex';
import mixin from '_common/mixins/ValidateForm';
import BoardGrid from '_common/components/board/BoardGrid.vue';
import DistributionByPercentage from '_common/shared/DistributionByPercentage.vue';
import { cloneDeep } from '_utils';
import Events from '_utils/events';
import FormatErrors from '_common/mixins/FormatErrors';
import workerManager from '_common/mixins/workerManager';
import PuzzleService from '_services/rest/modules/puzzle';
import {
  _LOAD_PUZZLE_NUMBER_SQUARE_MODEL_,
  CELL_SIZE,
  MODEL,
  OPERATOR_WEIGHTS,
} from './PuzzleNumberSquareModel';

export default {
  name: 'puzzle-number-square-form',
  components: { BoardGrid, DistributionByPercentage },
  mixins: [mixin, FormatErrors, workerManager],
  data() {
    return {
      model: cloneDeep(MODEL),
      errors: { name: { required: false } },
      cellSize: CELL_SIZE,
      operatorLabels: {
        plus: 'Soma (+)',
        minus: 'Subtração (-)',
        times: 'Multiplicação (×)',
        divide: 'Divisão (÷)',
      },
    };
  },
  computed: {
    enabledOperators() {
      return {
        plus: true,
        minus: true,
        times: true,
        divide: true,
      };
    },
  },
  watch: {
    'model.settings.enablePercentageDistribution': {
      handler(isEnabled) {
        if (!isEnabled) {
          this.applyDifficultyWeights();
        }
      },
    },
  },
  methods: {
    ...mapActions(['activeAlert', 'setHasChanges', 'setPuzzleNumberSquare']),
    generate() {
      this.cancelPreviousGeneration();
      this.startGenerationLoader();

      const worker = new NumberSquareWorker();
      this.workers.push(worker);

      worker.onmessage = ({ data }) => {
        if (data.status === 'finished') {
          this.model = {
            ...cloneDeep(MODEL),
            name: this.model.name,
            id: this.model.id,
            rows: data.rows,
            columns: data.columns,
            table: data.table,
            settings: { ...this.model.settings },
          };

          this.activeAlert({
            message: 'Puzzle gerado com sucesso!',
            theme: 'success',
          });
        } else if (data.status === 'error') {
          this.activeAlert({
            message: data.message || 'Não foi possível gerar o puzzle.',
            theme: 'warning',
          });
        }

        this.cleanupWorkers();
      };

      worker.onerror = err => {
        console.error('Worker Error:', err);
        this.activeAlert({
          message: 'Erro interno na geração.',
          theme: 'danger',
        });

        this.cleanupWorkers();
      };

      worker.postMessage({
        maxValue: this.model.settings.maxValue,
        difficulty: this.model.settings.difficulty,
        enablePercentageDistribution:
          this.model.settings.enablePercentageDistribution,
        operatorWeights: this.model.settings.operatorWeights,
      });
    },
    save() {
      if (!this.checkForm()) return;

      const puzzle = {
        ...this.model,
        slug: `${this.model.name.replace(/\s+/g, '-')}-number-square`,
      };

      PuzzleService.save(puzzle)
        .then(response => {
          this.activeAlert({
            message: 'Jogo salvo com sucesso!',
            theme: 'success',
          });
          this.setPuzzleNumberSquare(response);
          if (this.$route.name === 'admin-puzzle-number-square-new') {
            this.$router.push({
              name: 'admin-puzzle-number-square-edit',
              params: { puzzleId: response.id },
            });
          }
          this.setHasChanges(false);
        })
        .catch(error => {
          const errors = this.formatErrors(error.data);
          this.activeAlert({ message: errors, theme: 'danger' });
        });
    },
    applyDifficultyWeights() {
      const { difficulty } = this.model.settings;
      if (difficulty && OPERATOR_WEIGHTS[difficulty]) {
        this.model.settings.operatorWeights = {
          ...OPERATOR_WEIGHTS[difficulty],
        };
      }
    },
  },
  mounted() {
    Events.$on(_LOAD_PUZZLE_NUMBER_SQUARE_MODEL_, model => {
      const defaultWeights = {
        ...OPERATOR_WEIGHTS[model.settings.difficulty || 'easy'],
      };

      this.model = {
        ...model,
        settings: {
          enablePercentageDistribution: false,
          operatorWeights: defaultWeights,
          ...model.settings,
        },
      };
    });
  },
  destroyed() {
    Events.$off(_LOAD_PUZZLE_NUMBER_SQUARE_MODEL_);
  },
};
</script>
